import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import EditorialMarquee from "../components/Marquee";
import Footer from "../components/Footer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1640357897497-599b4fc84f51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzkxNzYyOTI4fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzkxNzYyOTI4fDA&ixlib=rb-4.1.0&q=85";
const BANNER_IMG =
  "https://images.unsplash.com/photo-1640357960494-9242650846d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBmdXJuaXR1cmV8ZW58MHx8fHwxNzkxNzYyOTI4fDA&ixlib=rb-4.1.0&q=85";

const HERO_LINES = ["Piccoli oggetti,", "grandi", "abitudini."];

const EASE = [0.16, 1, 0.3, 1];

function KineticLine({ children, delay }) {
  return (
    <span className="hero-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const aboutRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.12]);

  const { scrollYProgress: aboutProgress } = useScroll({
    target: aboutRef,
    offset: ["start end", "end start"],
  });
  const aboutY = useTransform(aboutProgress, [0, 1], ["-8%", "8%"]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 4)))
      .catch(() => {});
  }, []);

  const spans = [
    "md:col-span-4 h-[70vh] min-h-[420px]",
    "md:col-span-2 h-[70vh] min-h-[420px]",
    "md:col-span-2 h-[52vh] min-h-[360px]",
    "md:col-span-4 h-[52vh] min-h-[360px]",
  ];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden flex items-end">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Interno d'autore AB fëlise"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-black/40" />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pb-24 md:pb-32 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="overline-label mb-8"
            data-testid="hero-overline"
          >
            Articoli per la casa — selezionati con cura
          </motion.p>
          <h1
            className="font-serif font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl"
            data-testid="hero-heading"
          >
            <KineticLine delay={0.35}>{HERO_LINES[0]}</KineticLine>
            <KineticLine delay={0.5}>
              <span className="italic text-primary">{HERO_LINES[1]}</span>
            </KineticLine>
            <KineticLine delay={0.65}>{HERO_LINES[2]}</KineticLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="flex flex-wrap items-center gap-5 mt-12"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-collection"
              className="group inline-flex items-center gap-3 h-14 px-9 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
            >
              Esplora la collezione
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="#filosofia"
              data-testid="hero-cta-philosophy"
              className="inline-flex items-center gap-3 h-14 px-9 rounded-full border border-white/25 text-xs uppercase tracking-[0.25em] text-foreground hover:border-primary hover:text-primary transition-colors duration-300"
            >
              La filosofia
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-8 md:right-12 z-10 text-white/50"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }}>
            <ArrowDown size={18} strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* FEATURED BENTO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-24 md:pt-36" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline-label mb-5">I più amati</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-none">
              In evidenza
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors"
          >
            Tutta la collezione
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid md:grid-cols-6 gap-5">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} className={spans[i % spans.length]} />
          ))}
        </div>
      </section>

      {/* FILOSOFIA */}
      <section
        id="filosofia"
        ref={aboutRef}
        className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-48 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center"
        data-testid="philosophy-section"
      >
        <div className="relative overflow-hidden h-[60vh] min-h-[420px] border border-white/10">
          <motion.img
            src={ABOUT_IMG}
            alt="L'atelier AB fëlise"
            style={{ y: aboutY }}
            className="absolute inset-0 w-full h-[116%] object-cover"
          />
          <div className="absolute inset-0 bg-black/25" />
        </div>
        <div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <p className="overline-label mb-6">La filosofia</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] mb-10">
              L'utile, <br />
              <span className="italic text-primary">scelto con cura.</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed tracking-wide max-w-lg mb-6">
              AB fëlise shop nasce da un'idea semplice: gli oggetti che usiamo ogni giorno
              meritano la stessa attenzione di quelli che ammiriamo. Per questo selezioniamo
              dispenser, borracce, accessori per l'auto e piccoli alleati della casa
              testandoli uno a uno.
            </p>
            <p className="text-muted-foreground leading-relaxed tracking-wide max-w-lg mb-12">
              Niente cataloghi infiniti: pochi articoli, ben fatti, che risolvono
              un problema vero — e stanno bene in casa tua.
            </p>
            <div className="grid grid-cols-3 gap-8 max-w-lg">
              {[
                ["100+", "Articoli testati"],
                ["24h", "Spedizione rapida"],
                ["30gg", "Reso facile"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-4xl md:text-5xl font-light text-primary">{n}</p>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mt-3">
                    {l}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-48">
        <div className="relative overflow-hidden border border-white/10 h-[56vh] min-h-[380px] flex items-center">
          <img src={BANNER_IMG} alt="Collezione AB fëlise" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative z-10 px-8 md:px-16"
          >
            <p className="overline-label mb-6">Novità in catalogo</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-none max-w-2xl mb-10">
              Rendi la casa <span className="italic text-primary">più semplice.</span>
            </h2>
            <Link
              to="/shop"
              data-testid="banner-cta"
              className="group inline-flex items-center gap-3 h-14 px-9 rounded-full bg-foreground text-background text-xs uppercase tracking-[0.25em] font-medium hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            >
              Scopri i pezzi
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
